import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import axios from 'axios';
import { Trophy, Target, TrendUp, Globe, Skull, PencilSimple, Check, X, SoccerBall } from '@phosphor-icons/react';
import PrizeBanner from '../components/PrizeBanner';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const { user, refreshUser } = useAuth();
  const { t } = useTranslation();
  const [stats, setStats] = useState({ totalPoints: 0, weeklyRank: '-', monthlyRank: '-' });
  const [leaderboard, setLeaderboard] = useState([]);
  const [wcStatus, setWcStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState('');

  const handleSaveName = async () => {
    const trimmed = newName.trim();
    if (!trimmed || trimmed.length > 30) {
      toast.error(t('profile.nameError'));
      return;
    }
    try {
      await axios.put(`${API}/auth/display-name`, { display_name: trimmed });
      toast.success(t('profile.nameUpdated'));
      setEditingName(false);
      if (refreshUser) refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update name');
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [predictionsRes, leaderboardRes, wcStatusRes] = await Promise.all([
        axios.get(`${API}/predictions`),
        axios.get(`${API}/leaderboards/weekly`),
        axios.get(`${API}/wc/status`),
      ]);

      setLeaderboard(leaderboardRes.data.slice(0, 5));
      setWcStatus(wcStatusRes.data);

      const totalPoints = predictionsRes.data.reduce((sum, pred) => sum + pred.points_earned, 0);

      const weeklyData = await axios.get(`${API}/leaderboards/weekly`);
      const monthlyData = await axios.get(`${API}/leaderboards/monthly`);

      const weeklyRank = weeklyData.data.findIndex(item => item.user_id === user.id) + 1;
      const monthlyRank = monthlyData.data.findIndex(item => item.user_id === user.id) + 1;

      setStats({
        totalPoints,
        weeklyRank: weeklyRank > 0 ? weeklyRank : '-',
        monthlyRank: monthlyRank > 0 ? monthlyRank : '-',
      });
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-text-secondary">{t('common.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background noise-bg" data-testid="dashboard-page">
      <Navigation />

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
        {/* Welcome Header */}
        <div className="mb-8">
          <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter text-white flex items-center flex-wrap gap-x-3" data-testid="dashboard-welcome">
            <span>{t('dashboard.welcome')},</span>
            {editingName ? (
              <span className="inline-flex items-center gap-2">
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  maxLength={30}
                  autoFocus
                  data-testid="edit-display-name-input"
                  className="bg-background border-b-2 border-primary text-primary font-heading text-4xl sm:text-5xl font-black tracking-tighter outline-none w-48 sm:w-64"
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSaveName(); if (e.key === 'Escape') setEditingName(false); }}
                />
                <button onClick={handleSaveName} data-testid="save-display-name-btn" className="p-1.5 bg-primary/20 hover:bg-primary/40 rounded transition-all"><Check size={24} className="text-primary" /></button>
                <button onClick={() => setEditingName(false)} data-testid="cancel-edit-name-btn" className="p-1.5 bg-red-500/20 hover:bg-red-500/40 rounded transition-all"><X size={24} className="text-red-400" /></button>
              </span>
            ) : (
              <span className="inline-flex items-center gap-2">
                <span className="text-primary">{user.display_name || user.email.split('@')[0]}</span>
                <button
                  onClick={() => { setNewName(user.display_name || user.email.split('@')[0]); setEditingName(true); }}
                  data-testid="edit-display-name-btn"
                  className="p-1.5 hover:bg-primary/20 rounded transition-all"
                  title={t('profile.editName')}
                >
                  <PencilSimple size={20} className="text-text-muted hover:text-primary" />
                </button>
              </span>
            )}
          </h1>
          <p className="text-text-muted text-sm mt-1">{t('profile.clickToEdit')}</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-background-paper border border-border hover:border-primary/50 rounded-sm p-6 transition-all" data-testid="stat-total-points">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm uppercase tracking-wide text-text-secondary">{t('dashboard.totalPoints')}</span>
              <Target size={24} weight="bold" className="text-primary" />
            </div>
            <p className="font-mono text-4xl font-bold text-white">{stats.totalPoints}</p>
          </div>

          <div className="bg-background-paper border border-border hover:border-secondary/50 rounded-sm p-6 transition-all" data-testid="stat-weekly-rank">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm uppercase tracking-wide text-text-secondary">{t('dashboard.weeklyRank')}</span>
              <TrendUp size={24} weight="bold" className="text-secondary" />
            </div>
            <p className="font-mono text-4xl font-bold text-white">#{stats.weeklyRank}</p>
          </div>

          <div className="bg-background-paper border border-border hover:border-secondary/50 rounded-sm p-6 transition-all" data-testid="stat-monthly-rank">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm uppercase tracking-wide text-text-secondary">{t('dashboard.monthlyRank')}</span>
              <Trophy size={24} weight="bold" className="text-secondary" />
            </div>
            <p className="font-mono text-4xl font-bold text-white">#{stats.monthlyRank}</p>
          </div>
        </div>

        {/* Prize Banner */}
        <div className="mb-8">
          <PrizeBanner />
        </div>

        {/* Promo Cards */}
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            {/* World Cup Prediction Promo */}
            <Link to="/world-cup" className="block group" data-testid="promo-wc-prediction">
              <div className="relative bg-background-paper border border-[#FFD700]/30 hover:border-[#FFD700] rounded-sm p-6 transition-all overflow-hidden">
                <div className="absolute top-0 right-0 w-48 h-48 bg-[#FFD700]/5 rounded-full -translate-y-16 translate-x-16" />
                <div className="relative">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="flex items-center justify-center w-12 h-12 bg-[#FFD700]/10 rounded-sm">
                      <Globe size={28} weight="bold" className="text-[#FFD700]" />
                    </div>
                    <div>
                      <h2 className="font-heading text-2xl font-black tracking-tight text-white group-hover:text-[#FFD700] transition-colors">
                        {t('dashboardPromo.wcPredTitle')}
                      </h2>
                      <p className="text-xs uppercase tracking-widest text-[#FFD700] font-bold">{t('dashboardPromo.wcPredLive')}</p>
                    </div>
                  </div>
                  <p className="text-text-secondary mb-4">{t('dashboardPromo.wcPredDesc')}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs uppercase tracking-wide text-text-muted">
                      {t('worldCup.deadline')}: <span className="text-primary font-mono font-bold">June 10, 2026 BST</span>
                    </span>
                    <span className="text-xs uppercase tracking-wide text-text-muted">
                      {t('worldCup.predictionsCount')}: <span className="text-white font-mono font-bold">{wcStatus?.total_predictions || 0}</span>
                    </span>
                  </div>
                  <div className="mt-4">
                    <span className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#FFD700] text-black font-bold uppercase tracking-wide text-sm rounded-sm group-hover:scale-105 transition-transform">
                      {t('dashboardPromo.wcPredCta')}
                    </span>
                  </div>
                </div>
              </div>
            </Link>

            {/* WC Match Score Predictions Promo */}
            <Link to="/wc-match-predictions" className="block group" data-testid="promo-wc-match-predictions">
              <div className="relative bg-background-paper border border-emerald-500/30 hover:border-emerald-500 rounded-sm p-6 overflow-hidden transition-all">
                <div className="absolute top-0 right-0 w-48 h-48 bg-emerald-500/5 rounded-full -translate-y-16 translate-x-16" />
                <div className="relative">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="flex items-center justify-center w-12 h-12 bg-emerald-500/10 rounded-sm">
                      <SoccerBall size={28} weight="fill" className="text-emerald-400" />
                    </div>
                    <div>
                      <h2 className="font-heading text-2xl font-black tracking-tight text-white group-hover:text-emerald-400 transition-colors">
                        {t('dashboardPromo.wcMatchTitle')}
                      </h2>
                      <p className="text-xs uppercase tracking-widest text-emerald-400 font-bold">{t('dashboardPromo.wcPredLive')}</p>
                    </div>
                  </div>
                  <p className="text-text-secondary mb-4">{t('dashboardPromo.wcMatchDesc')}</p>
                  <div className="flex items-center gap-4">
                    <span className="bg-emerald-500 text-black font-black text-xs px-2 py-0.5 rounded">10 pts</span>
                    <span className="text-text-muted text-xs">{t('wcMatch.exactScore')}</span>
                    <span className="bg-emerald-500/60 text-black font-black text-xs px-2 py-0.5 rounded">3 pts</span>
                    <span className="text-text-muted text-xs">{t('wcMatch.correctResult')}</span>
                  </div>
                </div>
              </div>
            </Link>

            {/* WC Last Man Standing Promo */}
            <Link to="/wc-lms" className="block group" data-testid="promo-wc-lms">
              <div className="relative bg-background-paper border border-accent/30 hover:border-accent rounded-sm p-6 overflow-hidden transition-all">
                <div className="absolute top-0 right-0 w-48 h-48 bg-accent/5 rounded-full -translate-y-16 translate-x-16" />
                <div className="relative">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="flex items-center justify-center w-12 h-12 bg-accent/10 rounded-sm">
                      <Skull size={28} weight="bold" className="text-accent" />
                    </div>
                    <div>
                      <h2 className="font-heading text-2xl font-black tracking-tight text-white group-hover:text-accent transition-colors">
                        {t('dashboardPromo.wcLmsTitle')}
                      </h2>
                    </div>
                  </div>
                  <p className="text-text-secondary mb-4">{t('dashboardPromo.wcLmsDesc')}</p>
                  <div className="flex items-center gap-3">
                    <span className="text-xs uppercase tracking-wide text-text-muted">
                      {t('dashboardPromo.starts')}: <span className="text-accent font-mono font-bold">June 11, 2026</span>
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          </div>

          {/* Leaderboard */}
          <div className="bg-background-paper border border-border rounded-sm p-6" data-testid="leaderboard-section">
            <h2 className="font-heading text-2xl font-bold text-white mb-4">{t('dashboard.leaderboard')}</h2>
            <div className="space-y-2">
              {leaderboard.map((entry, index) => (
                <div
                  key={entry.user_id}
                  data-testid={`leaderboard-entry-${index}`}
                  className={`flex items-center justify-between p-3 rounded-sm ${
                    entry.user_id === user.id ? 'bg-primary/20 border border-primary' : 'bg-background-subtle'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <span className="font-mono font-bold text-lg w-6">
                      {index === 0 && <span className="text-primary">1</span>}
                      {index === 1 && <span className="text-secondary">2</span>}
                      {index === 2 && <span className="text-accent">3</span>}
                      {index > 2 && <span className="text-text-muted">{index + 1}</span>}
                    </span>
                    <span className="text-sm text-white truncate max-w-[120px]">
                      {entry.display_name || entry.email?.split('@')[0]}
                    </span>
                  </div>
                  <span className="font-mono font-bold text-primary">{entry.points}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
